import { ReactNode, useEffect } from 'react';

import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import CashlessConfirmForm from './confirmForm/CashlessConfirmForm';
import ConfirmForm from './confirmForm/ConfirmForm';
import styles from './ConfirmFundingView.module.css';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../../tracking/pageView';
import { getDrawdownType } from '../../drawDown/getDrawdownType';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { Tooltip } from '../../ui/tooltip';
import { ACTIVATE_CONFIRM_STEP } from '../Funding.actions';
import FundingHeader from '../fundingHeader/FundingHeader';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import { FundingProgressIndicator } from '../fundingSteps/FundingProgessIndicator/FundingProgressIndicator';
import LoadingOverlay from '../fundingSteps/LoadingOverlay';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activateConfirm: (
        offerId: string,
        drawdownAmount: number,
        sweep?: number
    ) => {
        dispatch({
            type: ACTIVATE_CONFIRM_STEP,
            offerId,
            amount: drawdownAmount,
            sweep
        });
    }
});

const ConfirmFundingView = ({
    activateConfirm
}: ReturnType<typeof mapDispatchToProps>) => {
    useTrackPageView({ name: 'Confirmed Funding' });

    const stateKey = useStateKey();

    const location = getCurrentLocation();

    let drawdownAmount: string | undefined | number = getQueryParameter(
        location,
        'amount'
    );
    const sweepPercentage = getQueryParameter(location, 'sweep');
    const sweep = sweepPercentage ? parseFloat(sweepPercentage) : undefined;

    const offerId: string | undefined = getQueryParameter(location, 'offerId');

    useEffect(() => {
        if (drawdownAmount !== undefined && offerId) {
            activateConfirm(offerId, drawdownAmount as number, sweep);
        }
    }, [activateConfirm, offerId, drawdownAmount, sweep]);

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    const isCashlessDrawdown = drawdownAmount === '0';

    const { isLoading: loadingAccounts, data: accounts } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'Get Query Param drawdown amount is undefined',
            {
                tags: {
                    reporter: 'confirm_funding view'
                }
            }
        );
    }

    if (!offerId) {
        IwDataDogLogger.logError('Get Query Param offerId is undefined', {
            tags: {
                reporter: 'confirm_funding view'
            }
        });
    }

    if (loadingFormalOffer || loadingAccounts) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}>
                    <LoadingOverlay />
                </div>
            </MainSectionWrapper>
        );
    }

    drawdownAmount = parseFloat(drawdownAmount!);

    const hasAccountsData = accounts?.data;

    if (formalOffer && hasAccountsData) {
        const flexiApprovalTypes = ['flexi24', 'credit_facility'];

        const outstandingAccount = accounts.data!.accounts.find(
            (account) =>
                account.loan_status &&
                account.loan_status !== 'ended' &&
                (account.product_type === formalOffer.product_type ||
                    //topups can be any flexi type eg. Ongoing flexi24 can have credit_facility topup
                    (flexiApprovalTypes.includes(account.product_type!) &&
                        flexiApprovalTypes.includes(formalOffer.product_type!)))
        );
        const isTopUp = !!outstandingAccount;

        return (
            <>
                {getCountry() === 'UK' && <FundingProgressIndicator step={3} />}

                <FundingHeader
                    header={
                        <HeaderMessage
                            formalOffer={formalOffer}
                            tooltip={
                                <span>
                                    <Tooltip
                                        className={styles.tooltip}
                                        message={
                                            <FormattedMessage
                                                id="_.confirm_funding.tooltip"
                                                defaultMessage="You can adjust your choice by clicking 'Go back' below."
                                            />
                                        }
                                        placement="auto"
                                    />
                                </span>
                            }
                        />
                    }
                    subHeader={<SubHeaderMessage formalOffer={formalOffer} />}
                    returnPath="/funding-requirements/"
                />
                {isCashlessDrawdown ? (
                    <CashlessConfirmForm formalOffer={formalOffer!} />
                ) : (
                    <ConfirmForm
                        drawdownAmount={drawdownAmount}
                        selectedInputAmount={drawdownAmount}
                        selectedSweepPercentage={sweep!}
                        offerId={offerId!}
                        isTopUp={isTopUp}
                        formalOffer={formalOffer}
                        outstandingAccount={outstandingAccount}
                    />
                )}
            </>
        );
    }
    return null;
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(ConfirmFundingView);

const HeaderMessage = ({
    formalOffer,
    tooltip
}: {
    formalOffer: TFormalOffer;
    tooltip: ReactNode;
}) => {
    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.confirm_funding.repayment_reduction_header"
                    defaultMessage="We are ready to reduce your monthly repayments"
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.confirm_funding.interestOnlyHeader"
                    defaultMessage="We are ready to extend your loan{tooltip}"
                    values={{ tooltip }}
                />
            );
        case 'revolving_credit_facility_first_drawdown':
            return (
                <FormattedMessage
                    id="_.confirm_funding.rcf_first_drawdown_header"
                    defaultMessage="Confirm your first payout"
                />
            );
        case 'revolving_credit_facility_subsequent_drawdown':
            return (
                <FormattedMessage
                    id="_.confirm_funding.rcf_subsequent_drawdown_header"
                    defaultMessage="We are ready to transfer the money to your account."
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.confirm_funding.header"
                    defaultMessage="We're all set to send funds to your account{tooltip}"
                    values={{ tooltip }}
                />
            );
    }
};

const SubHeaderMessage = ({ formalOffer }: { formalOffer: TFormalOffer }) => {
    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.confirm_funding.repayment_reduction_subheader"
                    defaultMessage="Click on confirm to reduce your monthly repayments. We're ready when you are!"
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.confirm_funding.interestOnlySubHeader"
                    defaultMessage="Just hit ‘Confirm’ to extend your loan."
                />
            );
        case 'revolving_credit_facility_first_drawdown':
            return (
                <FormattedMessage
                    id="_.confirm_funding.rcf_first_drawdown_subheader"
                    defaultMessage="Click on ‘Confirm Payout’ to receive your first payout and activate your credit line. You can withdraw money again at any time afterwards."
                />
            );
        case 'revolving_credit_facility_subsequent_drawdown':
            return (
                <FormattedMessage
                    id="_.confirm_funding.rcf_subsequent_drawdown_subheader"
                    defaultMessage="Simply click ‘Confirm’ to transfer the money to your account. We are ready when you are!"
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.confirm_funding.subheader"
                    defaultMessage="Just hit ‘Confirm’ to send the funds to your account."
                />
            );
    }
};
